/**
 * The ReCheck-for-Discharge gate's read of `GET /provider/document/check-discharge-list/{eocId}`.
 *
 * THIN-7875: that endpoint returns a `CheckDischargeListResponse` **object** —
 * `{ documents, recheckDischargeApproval }` — and has done since THIN-7586 added the approval
 * status. The therapist-dashboard entry point was still testing `response.length` on it. An object
 * has no `length`, so the test was permanently `undefined`, the modal never opened, and ReCheck did
 * nothing at all from the dashboard for every tenant with the toggle on — while the patient-details
 * path, reading `response.documents`, gated correctly. Two copies of the same read is what let them
 * drift, so both entry points go through this function instead.
 */
export type RecheckDischargeDecision = {
  approvalStatus: string | undefined;
  isApproved: boolean;
  nothingOutstanding: boolean;
  /** True when the discharge may proceed without showing the review modal at all. */
  canSkipModal: boolean;
};

export const getRecheckDischargeDecision = (response: any): RecheckDischargeDecision => {
  const approvalStatus = response?.recheckDischargeApproval?.status;
  const isApproved = approvalStatus === 'APPROVED';
  // THIN-7873: nothing outstanding at all. The BE now drops an overdue recert/WPN from this list
  // when no submitted note with minutes followed its due date, so an empty list is the common
  // outcome for exactly the population that change unblocks, and a review modal over an empty
  // table is pure friction — there is nothing to review.
  //
  // An absent or non-array `documents` is deliberately NOT "nothing outstanding": the response has
  // either not loaded or is not the shape we expect, and a gate must fail closed. Treating an
  // unrecognised shape as "nothing to see" is precisely the bug this module exists to prevent.
  const nothingOutstanding = Array.isArray(response?.documents) && response.documents.length === 0;
  return {
    approvalStatus,
    isApproved,
    nothingOutstanding,
    canSkipModal: isApproved || nothingOutstanding,
  };
};
